/**
 * BulkActionsToolbar Component
 * 
 * Bulk operations toolbar that appears when items are selected
 * Shared component used by CategoriesList and TagsList
 */

import React from 'react';
import { Trash2, Edit, Move, X } from 'lucide-react';

interface BulkActionsToolbarProps {
  selectedCount: number;
  onBulkDelete: () => void;
  onBulkEdit: () => void;
  onBulkMove?: (parentId: string) => void;
  onClearSelection: () => void;
  availableActions: ('delete' | 'edit' | 'move')[];
}

export function BulkActionsToolbar({
  selectedCount,
  onBulkDelete,
  onBulkEdit,
  onBulkMove,
  onClearSelection,
  availableActions
}: BulkActionsToolbarProps): React.ReactElement {
  return (
    <div className="bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 rounded-lg p-4 flex items-center justify-between">
      <div className="flex items-center space-x-4">
        <span className="text-sm font-medium text-primary-900 dark:text-primary-100">
          {selectedCount} {selectedCount === 1 ? 'item' : 'items'} selected
        </span>
        <div className="flex items-center space-x-2">
          {availableActions.includes('edit') && (
            <button
              onClick={onBulkEdit}
              className="flex items-center space-x-2 px-3 py-1.5 text-sm font-medium text-primary-700 dark:text-primary-300 hover:bg-primary-100 dark:hover:bg-primary-900/40 rounded-lg transition-colors"
              aria-label="Edit selected items"
            >
              <Edit className="w-4 h-4" aria-hidden="true" />
              <span>Edit</span>
            </button>
          )}
          {availableActions.includes('move') && onBulkMove && (
            <button
              onClick={() => onBulkMove('')}
              className="flex items-center space-x-2 px-3 py-1.5 text-sm font-medium text-primary-700 dark:text-primary-300 hover:bg-primary-100 dark:hover:bg-primary-900/40 rounded-lg transition-colors"
              aria-label="Move selected items"
            >
              <Move className="w-4 h-4" aria-hidden="true" />
              <span>Move</span>
            </button>
          )}
          {availableActions.includes('delete') && (
            <button
              onClick={onBulkDelete}
              className="flex items-center space-x-2 px-3 py-1.5 text-sm font-medium text-red-700 dark:text-red-300 hover:bg-red-100 dark:hover:bg-red-900/40 rounded-lg transition-colors"
              aria-label="Delete selected items"
            >
              <Trash2 className="w-4 h-4" aria-hidden="true" />
              <span>Delete</span>
            </button>
          )}
        </div>
      </div>
      <button
        onClick={onClearSelection}
        className="p-1.5 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
        aria-label="Clear selection"
      >
        <X className="w-4 h-4" aria-hidden="true" />
      </button>
    </div>
  );
}

